import { getValidJwt, clearJwt } from "./auth";
import type { IpListItem, IpDetail, LoginResponse, FavoritesResponse } from "@/types";

const BASE = "/api";

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

// /auth/* は未認証で叩く API なので、401 でも JWT クリアや /login redirect は行わない
// (login 時の「資格情報が違う」エラーをそのまま表示できるようにする)。
function isAuthEndpoint(path: string): boolean {
  return path.startsWith("/auth/");
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const jwt = getValidJwt();
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      ...(init.headers || {}),
    },
  });

  let body: unknown = null;
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    body = await res.json().catch(() => null);
  }

  if (res.status === 401 && !isAuthEndpoint(path)) {
    clearJwt();
    window.location.assign("/login");
    const msg = (body as { error?: string } | null)?.error || "unauthorized";
    throw new ApiError(401, msg, body);
  }

  if (!res.ok) {
    const msg = (body as { error?: string } | null)?.error || `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, body);
  }

  return body as T;
}

export const api = {
  login: (email: string, password: string) =>
    request<LoginResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  setPassword: (token: string, password: string) =>
    request<{ status: string; user_id: string }>("/auth/set-password", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    }),
  listIps: () => request<{ ips: IpListItem[] }>("/ips"),
  getIp: (ip_id: string) => request<IpDetail>(`/ips/${encodeURIComponent(ip_id)}`),
  getFavorites: () => request<FavoritesResponse>("/me/favorites"),
  addFavorite: (ip_id: string) =>
    request<FavoritesResponse>(`/me/favorites/${encodeURIComponent(ip_id)}`, { method: "PUT" }),
  removeFavorite: (ip_id: string) =>
    request<FavoritesResponse>(`/me/favorites/${encodeURIComponent(ip_id)}`, { method: "DELETE" }),
  createUser: (email: string, role: string, name?: string) =>
    request<{ user_id: string; email: string }>("/admin/users", {
      method: "POST",
      body: JSON.stringify({ email, role, name }),
    }),
};
